"use client";

import React, { useEffect, useRef, useState } from "react";
import { DotButton, useDotButton } from "./Dots";
import { PrevButton, NextButton, usePrevNextButtons } from "./Arrows";
import useEmblaCarousel from "embla-carousel-react";
import If from "./If";
import Play from "./icons/Play";

function Video({
  src = "",
  title = "Illuminus Video",
  onVideoEnd,
  ...props
}: {
  src?: string;
  title?: string;
  onVideoEnd?: () => void;
}) {
  const ref = useRef(null);
  const iframeRef = useRef(null);
  const [playerSrc, setPlayerSrc] = useState(src);
  const [player, setPlayer] = useState(null);
  const [state, setState] = useState("idle");
  const backgroundEle = useRef(null);

  useEffect(() => {
    if (!iframeRef.current || state !== "playing") return;

    // Load Vimeo Player API
    const script = document.createElement("script");
    script.src = "https://player.vimeo.com/api/player.js";
    script.async = true;
    
    const handleScriptLoad = () => {
      if (window.Vimeo && iframeRef.current) {
        const vimeoPlayer = new window.Vimeo.Player(iframeRef.current);
        
        vimeoPlayer.on("ended", () => {
          if (onVideoEnd) {
            onVideoEnd();
          }
        });
      }
    };

    if (window.Vimeo) {
      handleScriptLoad();
    } else {
      script.onload = handleScriptLoad;
      document.head.appendChild(script);
    }

    return () => {
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [state, onVideoEnd]);

  return (
    <div key={playerSrc} className="relative z-0">
      <div
        className="relative z-[1] overflow-hidden"
        ref={backgroundEle}
      >
        <iframe
          ref={iframeRef}
          width="100%"
          height="100%"
          src={`${playerSrc}&controls=0`}
          allow="autoplay; fullscreen; picture-in-picture"
          allowFullScreen
          className="aspect-video w-full"
          loading="lazy"
          title={title}
        />

        <If condition={state !== "playing"}>
          <div className="absolute inset-0 rounded overflow-hidden z-10">
            <span className="sr-only">Play video</span>
            <button
              className="absolute bottom-10 left-10  z-10 w-[88px]"
              onClick={() => {
                if (state === "playing") {
                  setPlayerSrc(src);
                } else {
                  setState("playing");
                  setPlayerSrc(
                    src + "&autoplay=1&controls=1"
                  );
                }
              }}
            >
              <span className="sr-only">Play</span>
              <Play width={88} />
            </button>
          </div>
        </If>
      </div>
    </div>
  );
}

const Slide = ({ src, onVideoEnd }: { src: string; onVideoEnd?: () => void }) => {
  const [state, setState] = useState("idle");
  return (
    <div className="embla__slide flex-[0_0_100dvw]">
      <Video
        src={src}
        onVideoEnd={onVideoEnd}
      />
    </div>
  );
};

const EmblaCarousel = (props) => {
  const { videos, options, title, subtitle, description } = props;
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "start",
    containScroll: false,
  });

  const { selectedIndex, scrollSnaps, onDotButtonClick } =
    useDotButton(emblaApi);

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi);

  

  return (
    <section className="gallery min-h-[50dvh] flex flex-col gap-20 overflow-hidden">
      <div className="relative">
        <div className="embla__viewport" ref={emblaRef}>
          <div className="embla__container w-max flex gap-4 items-center">
            {videos.map((video) => (
              <Slide
                key={video?.value}
                src={video?.value}
                onVideoEnd={() => {
                  if (emblaApi?.canScrollNext()) {
                    emblaApi.scrollNext();
                  }
                }}
              />
            ))}
          </div>
        </div>
        {emblaApi?.canScrollPrev() && (
          <PrevButton onClick={onPrevButtonClick} disabled={prevBtnDisabled} />
        )}
        {emblaApi?.canScrollNext() && (
          <NextButton onClick={onNextButtonClick} disabled={nextBtnDisabled} />
        )}
      </div>

      {/* <div className="embla__controls">
        <div className="embla__dots">
          {scrollSnaps.map((_, index) => (
            <DotButton
              key={index}
              onClick={() => onDotButtonClick(index)}
              className={"embla__dot".concat(
                index === selectedIndex ? " embla__dot--selected" : ""
              )}
            />
          ))}
        </div>
      </div> */}
    </section>
  );
};

export default EmblaCarousel;
